import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Filter, FilterX, Loader2, CheckSquare, Mail, X, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { CustomDatePicker } from '../components/ui/custom-date-picker';
import { CLIENT_OPTIONS } from '../components/silica-loads/constants';
import { PaginationControls } from '../components/silica-loads/PaginationControls';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

const colorBadge = (color) => {
  if (color === 'red') return 'bg-red-100 text-red-700 border-red-300';
  if (color === 'yellow') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  return '';
};

const colorLabel = (color) => {
  if (color === 'red') return 'Past Due';
  if (color === 'yellow') return 'Overdue';
  return '';
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Get the pending (unpaid) amount for a bill — for dual-invoice partially_paid, only the unpaid invoice amount
const getPendingAmount = (bill) => {
  if (bill.bill_status === 'partially_paid' && bill.invoice_amount_1 != null && bill.invoice_amount_2 != null) {
    const s1 = (bill.bill_status_1 || 'pending').toLowerCase();
    const s2 = (bill.bill_status_2 || 'pending').toLowerCase();
    let unpaid = 0;
    if (s1 !== 'paid') unpaid += Number(bill.invoice_amount_1) || 0;
    if (s2 !== 'paid') unpaid += Number(bill.invoice_amount_2) || 0;
    return unpaid;
  }
  return Number(bill.invoice_amount) || 0;
};

// Get invoice numbers — for partially_paid dual-invoice, only show unpaid invoice numbers
const getInvoiceNumbers = (bill) => {
  const nums = [];
  if (bill.bill_status === 'partially_paid' && bill.invoice_amount_1 != null && bill.invoice_amount_2 != null) {
    const s1 = (bill.bill_status_1 || 'pending').toLowerCase();
    const s2 = (bill.bill_status_2 || 'pending').toLowerCase();
    if (s1 !== 'paid') {
      const n = bill.generated_invoice_number_1 || bill.invoice_number_1;
      if (n) nums.push(n);
    }
    if (s2 !== 'paid') {
      const n = bill.generated_invoice_number_2 || bill.invoice_number_2;
      if (n) nums.push(n);
    }
    return nums;
  }
  // Manual uploads
  if (bill.invoice_number) nums.push(bill.invoice_number);
  if (bill.addition_invoice_number) nums.push(bill.addition_invoice_number);
  if (bill.deduction_invoice_number) nums.push(bill.deduction_invoice_number);
  // Generated invoices
  if (bill.generated_invoice_number && !nums.includes(bill.generated_invoice_number)) nums.push(bill.generated_invoice_number);
  if (bill.generated_addition_invoice_number && !nums.includes(bill.generated_addition_invoice_number)) nums.push(bill.generated_addition_invoice_number);
  if (bill.generated_deduction_invoice_number && !nums.includes(bill.generated_deduction_invoice_number)) nums.push(bill.generated_deduction_invoice_number);
  // Multi-invoice flow (invoice_count = 2)
  if (bill.invoice_number_1 && !nums.includes(bill.invoice_number_1)) nums.push(bill.invoice_number_1);
  if (bill.invoice_number_2 && !nums.includes(bill.invoice_number_2)) nums.push(bill.invoice_number_2);
  if (bill.generated_invoice_number_1 && !nums.includes(bill.generated_invoice_number_1)) nums.push(bill.generated_invoice_number_1);
  if (bill.generated_invoice_number_2 && !nums.includes(bill.generated_invoice_number_2)) nums.push(bill.generated_invoice_number_2);
  // Updated invoice
  if (bill.updated_invoice_number && !nums.includes(bill.updated_invoice_number)) nums.push(bill.updated_invoice_number);
  if (bill.generated_updated_invoice_number && !nums.includes(bill.generated_updated_invoice_number)) nums.push(bill.generated_updated_invoice_number);
  // SVE summary
  if (bill.summary_generated_invoice_number && !nums.includes(bill.summary_generated_invoice_number)) nums.push(bill.summary_generated_invoice_number);
  return nums;
};

export default function PendingBills() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    client_name: 'all',
    status_filter: 'all',
  });

  // Select mode state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [clientEmailInputs, setClientEmailInputs] = useState({});
  const [clientEmails, setClientEmails] = useState({});
  const [clientEmailErrors, setClientEmailErrors] = useState({});
  const [sending, setSending] = useState(false);

  const hasActiveFilters = filters.date_from || filters.date_to || filters.client_name !== 'all' || filters.status_filter !== 'all';

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page, page_size: 20 });
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.client_name !== 'all') params.append('client_name', filters.client_name);
      if (filters.status_filter === 'red') {
        params.append('view', 'past_due');
        params.append('status_filter', 'red');
      } else {
        params.append('view', 'all');
      }
      const { data } = await axios.get(`${API}/silica-loads/pending-bills/list?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBills(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch {
      setBills([]);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchBills(); }, [fetchBills]);

  const clearFilters = () => {
    setFilters({ date_from: '', date_to: '', client_name: 'all', status_filter: 'all' });
    setPage(1);
    setShowFilters(false);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    // Parse date string manually to avoid timezone issues
    const parts = d.split('T')[0].split('-');
    if (parts.length >= 3) {
      const dt = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    const dt = new Date(d);
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (v) => {
    if (!v && v !== 0) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
  };

  // Select mode handlers
  const toggleSelectMode = () => {
    if (selectMode) {
      setSelectedIds(new Set());
    }
    setSelectMode(!selectMode);
  };

  const toggleSelect = (loadId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(loadId)) next.delete(loadId);
      else next.add(loadId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === bills.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(bills.map(b => b.load_id)));
    }
  };

  // Email handlers
  const addEmailForClient = (client) => {
    const trimmed = (clientEmailInputs[client] || '').trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setClientEmailErrors(p => ({ ...p, [client]: 'Please enter a valid email address' }));
      return;
    }
    const existing = clientEmails[client] || [];
    if (existing.includes(trimmed)) {
      setClientEmailErrors(p => ({ ...p, [client]: 'Email already added' }));
      return;
    }
    setClientEmails(p => ({ ...p, [client]: [...existing, trimmed] }));
    setClientEmailInputs(p => ({ ...p, [client]: '' }));
    setClientEmailErrors(p => ({ ...p, [client]: '' }));
  };

  const removeEmailForClient = (client, email) => {
    setClientEmails(p => ({ ...p, [client]: (p[client] || []).filter(e => e !== email) }));
  };

  const handleClientEmailKeyDown = (e, client) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmailForClient(client);
    }
  };

  const openEmailDialog = () => {
    const initEmails = {};
    const initInputs = {};
    const initErrors = {};
    selectedClients.forEach(c => { initEmails[c] = []; initInputs[c] = ''; initErrors[c] = ''; });
    setClientEmails(initEmails);
    setClientEmailInputs(initInputs);
    setClientEmailErrors(initErrors);
    setShowEmailDialog(true);
  };

  const selectedClients = [...new Set(bills.filter(b => selectedIds.has(b.load_id)).map(b => b.client_name))];

  const allClientsHaveEmails = selectedClients.every(c => (clientEmails[c] || []).length > 0);

  const sendEmails = async () => {
    // Validate all clients have at least one email
    let hasError = false;
    const newErrors = {};
    selectedClients.forEach(c => {
      if (!(clientEmails[c] || []).length) {
        newErrors[c] = 'Add at least one email address';
        hasError = true;
      }
    });
    if (hasError) {
      setClientEmailErrors(p => ({ ...p, ...newErrors }));
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API}/email/send-selected-bills`, {
        load_ids: Array.from(selectedIds),
        client_emails: clientEmails,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(data.message || 'Emails sent successfully');
      setShowEmailDialog(false);
      setSelectMode(false);
      setSelectedIds(new Set());
      fetchBills();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6 pb-24 sm:pb-6" data-testid="pending-bills-page">
        {/* Header */}
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase mb-1">Finance</p>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Pending Bills
          </h1>
          <p className="text-sm text-slate-500 mt-1">Track unpaid silica load invoices</p>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="toggle-filters-btn"
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`rounded-xl h-9 w-9 p-0 ${showFilters ? 'border-blue-500 text-blue-600 bg-blue-50' : ''}`}
            title="Filter"
          >
            <Filter className="w-4 h-4" />
          </Button>
          {hasActiveFilters && (
            <Button
              data-testid="clear-filters-btn"
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="rounded-xl h-9 w-9 p-0 border-red-300 text-red-600 hover:bg-red-50"
              title="Clear Filters"
            >
              <FilterX className="w-4 h-4" />
            </Button>
          )}

          {/* Select Button */}
          <Button
            data-testid="select-mode-btn"
            variant="outline"
            size="sm"
            onClick={toggleSelectMode}
            className={`rounded-xl h-9 px-3 gap-1.5 text-xs font-semibold ${selectMode ? 'border-blue-500 text-blue-600 bg-blue-50' : ''}`}
          >
            <CheckSquare className="w-3.5 h-3.5" />
            {selectMode ? 'Cancel' : 'Select'}
          </Button>

          {/* Send Email Button */}
          <Button
            data-testid="send-email-btn"
            variant="outline"
            size="sm"
            disabled={selectedIds.size === 0}
            onClick={openEmailDialog}
            className={`rounded-xl h-9 px-3 gap-1.5 text-xs font-semibold ${selectedIds.size > 0 ? 'border-slate-500 text-slate-700 bg-slate-50 hover:bg-slate-100' : 'opacity-50 cursor-not-allowed'}`}
          >
            <Mail className="w-3.5 h-3.5" />
            Send Email{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>

          {/* Legend */}
          <div className="flex items-center gap-3 ml-auto text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-yellow-400 inline-block" /> Overdue</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Past Due</span>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="p-4 rounded-2xl border shadow-sm" data-testid="filters-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Date From</Label>
                <CustomDatePicker
                  value={filters.date_from}
                  onChange={(d) => { setFilters(p => ({ ...p, date_from: d || '' })); setPage(1); }}
                  placeholder="Start date"
                  allowFuture={true}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Date To</Label>
                <CustomDatePicker
                  value={filters.date_to}
                  onChange={(d) => { setFilters(p => ({ ...p, date_to: d || '' })); setPage(1); }}
                  placeholder="End date"
                  allowFuture={true}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Client</Label>
                <Select value={filters.client_name} onValueChange={(v) => { setFilters(p => ({ ...p, client_name: v })); setPage(1); }}>
                  <SelectTrigger className="h-10 rounded-xl" data-testid="filter-client"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {CLIENT_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Status</Label>
                <Select value={filters.status_filter} onValueChange={(v) => { setFilters(p => ({ ...p, status_filter: v })); setPage(1); }}>
                  <SelectTrigger className="h-10 rounded-xl" data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="red">Past Due</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        )}

        {/* Summary */}
        <p className="text-sm text-slate-500">{total} pending bill{total !== 1 ? 's' : ''} found</p>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : bills.length === 0 ? (
          <Card className="p-12 text-center rounded-2xl">
            <p className="text-slate-500 text-lg font-medium">No pending bills found</p>
            <p className="text-slate-400 text-sm mt-1">All invoices are paid or no loads match your filters</p>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-max text-sm" data-testid="pending-bills-table">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                  {selectMode && (
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        data-testid="select-all-checkbox"
                        checked={bills.length > 0 && selectedIds.size === bills.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 whitespace-nowrap">Date</th>
                  <th className="px-4 py-3 whitespace-nowrap">Client</th>
                  <th className="px-4 py-3 whitespace-nowrap">PO</th>
                  <th className="px-4 py-3 whitespace-nowrap">Vehicle</th>
                  <th className="px-4 py-3 whitespace-nowrap">Invoice No.</th>
                  <th className="px-4 py-3 whitespace-nowrap">Date Received</th>
                  <th className="px-4 py-3 whitespace-nowrap">Invoice Amt</th>
                  <th className="px-4 py-3 whitespace-nowrap">Bill Status</th>
                  <th className="px-4 py-3 whitespace-nowrap">Delivery</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bills.map((bill) => (
                  <tr
                    key={bill.load_id}
                    className={`hover:bg-slate-50 transition-colors ${selectMode && selectedIds.has(bill.load_id) ? 'bg-slate-50/50' : ''}`}
                    data-testid={`bill-row-${bill.load_id}`}
                  >
                    {selectMode && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          data-testid={`select-checkbox-${bill.load_id}`}
                          checked={selectedIds.has(bill.load_id)}
                          onChange={() => toggleSelect(bill.load_id)}
                          className="w-4 h-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {colorLabel(bill.color) ? (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${colorBadge(bill.color)}`}>
                          {colorLabel(bill.color)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(bill.date)}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{bill.client_name || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{bill.purchase_order || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{bill.vehicle_number || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {(() => {
                        const nums = getInvoiceNumbers(bill);
                        return nums.length > 0 ? nums.join(', ') : '-';
                      })()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(bill.date_received)}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium">{formatCurrency(getPendingAmount(bill))}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${bill.bill_status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                        {bill.bill_status ? bill.bill_status.charAt(0).toUpperCase() + bill.bill_status.slice(1) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${bill.delivery_status === 'delivered' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {bill.delivery_status === 'delivered' ? 'Delivered' : 'In Progress'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-bold text-sm border-t-2 border-slate-300">
                  {selectMode && <td className="px-3 py-3" />}
                  <td colSpan={7} className="px-4 py-3 text-right font-bold text-slate-700">Total</td>
                  <td className="px-4 py-3 whitespace-nowrap font-bold text-slate-900" data-testid="pending-bills-total-amount">
                    {formatCurrency(bills.reduce((sum, b) => sum + getPendingAmount(b), 0))}
                  </td>
                  <td colSpan={2} className="px-4 py-3" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Pagination */}
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={total}
          pageSize={20}
        />
      </div>

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Send Payment Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Selected info */}
            <div className="bg-slate-50 rounded-xl p-3 text-sm">
              <p className="text-slate-600">
                <span className="font-semibold">{selectedIds.size}</span> bill{selectedIds.size !== 1 ? 's' : ''} selected
                {selectedClients.length > 1 && (
                  <span className="text-slate-600 font-medium"> — separate emails per client</span>
                )}
              </p>
            </div>

            {/* Per-client email sections */}
            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
              {selectedClients.map((client) => (
                <div key={client} className="space-y-2">
                  <Label className="text-sm font-bold text-slate-800">{client}</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid={`email-input-${client}`}
                      type="email"
                      value={clientEmailInputs[client] || ''}
                      onChange={(e) => { setClientEmailInputs(p => ({ ...p, [client]: e.target.value })); setClientEmailErrors(p => ({ ...p, [client]: '' })); }}
                      onKeyDown={(e) => handleClientEmailKeyDown(e, client)}
                      placeholder="Enter email address"
                      className={`h-10 rounded-xl flex-1 ${clientEmailErrors[client] ? 'border-red-400' : ''}`}
                    />
                    <Button
                      data-testid={`add-email-btn-${client}`}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addEmailForClient(client)}
                      className="h-10 rounded-xl px-3"
                    >
                      Add
                    </Button>
                  </div>
                  {clientEmailErrors[client] && <p className="text-xs text-red-500">{clientEmailErrors[client]}</p>}
                  {(clientEmails[client] || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {clientEmails[client].map((email) => (
                        <div key={email} className="flex items-center gap-1 bg-slate-100 text-slate-800 px-2.5 py-1 rounded-lg text-xs">
                          <span>{email}</span>
                          <button type="button" onClick={() => removeEmailForClient(client, email)} className="text-slate-600 hover:text-slate-800 ml-0.5">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedClients.length > 1 && <div className="border-b border-slate-100 pt-1" />}
                </div>
              ))}
            </div>

            {/* Send button */}
            <Button
              data-testid="send-btn"
              onClick={sendEmails}
              disabled={!allClientsHaveEmails || sending}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-slate-600 to-slate-800 hover:from-slate-700 hover:to-slate-900 text-white font-semibold"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Email{selectedClients.length > 1 ? 's' : ''}</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
